/**
 * Time-zone-aware date helpers.
 *
 * Reads the company's TimeZoneId from the Vuex `company` module and delegates
 * to dateHelpers.core.mjs for the pure formatting logic.
 */

import store from "@/state/store";
import {
  DEFAULT_TZ,
  isCivilDateString,
  todayInTz,
  daysAgoInTz,
  nowInTz,
} from "@/helpers/dateHelpers.core.mjs";

export { DEFAULT_TZ, isCivilDateString };

/**
 * Returns the company TZ from the Vuex store, falling back to DEFAULT_TZ if the
 * store isn't ready (early bootstrap, tests, etc). Exported so other helpers
 * (formatters.js) don't duplicate this resolution.
 */
export function currentTz(override) {
  if (override) return override;
  try {
    return store?.getters?.["company/timeZoneId"] || DEFAULT_TZ;
  } catch (_) {
    return DEFAULT_TZ;
  }
}

/**
 * Returns the current civil date in the company time zone as `yyyy-MM-dd`.
 * Prefer this over `new Date().toISOString().slice(0, 10)` for any "today"
 * prefill — that idiom returns UTC and is wrong at midnight crossover.
 */
export function todayInCompanyTz(tz) {
  return todayInTz(currentTz(tz));
}

/**
 * Returns the current civil date `n` days ago in the company TZ as `yyyy-MM-dd`.
 */
export function daysAgoInCompanyTz(n, tz) {
  return daysAgoInTz(n, currentTz(tz));
}

/**
 * Returns "now" in the company TZ as `yyyy-MM-dd HH:mm:ss`.
 */
export function nowInCompanyTz(tz) {
  return nowInTz(currentTz(tz));
}
